import React, { useState } from "react";
import { Chip, Typography } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllClientPocsViewModel } from "./AllClientPocsViewModel";
import AddClientPoc from "./AddClientPoc";
import { useRbac } from "../../hooks/RbacContext";


const AllClientPocs = () => {
  const { clientPocs,
    createClientPoc,
    updateClientPoc,
    getCountryName,
    countries,
    handleDelete, clients, getClientName, refetch } =
    useAllClientPocsViewModel();
  const { showLoading, hideLoading } = useLoading();
  const [isAddClientPocOpen, setIsAddClientPocOpen] = useState(false);
  const [selectedClientPoc, setSelectedClientPoc] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "client_id",
      label: "Client Name",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getClientName(value)}
        </Typography>
      ),
      searchValue: (value) => getClientName(value),
    },
    {
      id: "poc_name",
      label: "Name",
      align: "left",
    },
    {
      id: "poc_email",
      label: "Email",
      align: "left",
    },
    {
      id: "poc_designation",
      label: "Designation",
      align: "left",
    },
    {
      id: "poc_mobile",
      label: "Mobile",
      align: "left",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => (value ? "active" : "inactive"),
    },
  ];

  const fixedColumns = {
    left: [],
    right: ["is_active", "actions"],
  };

  const handleAddClientPoc = () => {
    setSelectedClientPoc(null);
    setIsAddClientPocOpen(true);
  };

  const handleEditClientPoc = (country) => {
    setSelectedClientPoc(country);
    setIsAddClientPocOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddClientPocOpen(false);
    setSelectedClientPoc(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedClientPoc) {
        await updateClientPoc(selectedClientPoc.client_poc_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Poc updated successfully",
        });
      } else {
        await createClientPoc(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Poc added successfully",
        });
      }
      await refetch();
      handleCloseDialog();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (country) => {
    setDeleteConfirmation({
      country,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const country = deleteConfirmation.country;
    try {
      showLoading();
      await handleDelete(country);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Country deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete country",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (country) => {
    const actions = [];
    if (checkAccess("clientPOC", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClientPoc,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("clientPOC", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  const columnFilters = [
    {
      id: "client_id",
      type: "select",
      label: "Client Name",
      render: (value) => (getClientName(value)),
      searchValue: (value) => (getClientName(value)),
      icon: <PublicIcon />,
      sendIcon: true,
    },
    {
      id: "country_id",
      type: "select",
      label: "Country",
      render: (value) => getCountryName(value),
      icon: <PublicIcon />,
      sendIcon: true,
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (value ? "Active" : "Inactive"),
      searchValue: (value) => (value ? "active" : "inactive"),
      icon: <CircleIcon />
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={clientPocs}
        onAdd={handleAddClientPoc}
        showActions={true}
        showAddButton={checkAccess("clientPOC","add")}
        addButtonIcon={<PublicIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Client POCs"
        subtitle="Manage and monitor all Client pocs"
        addButtonText="Add Client POC"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddClientPoc
        open={isAddClientPocOpen}
        onClose={handleCloseDialog}
        initialData={selectedClientPoc}
        onSubmit={handleSubmit}
        clients={clients}
        countries={countries}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Country"
          message={`Are you sure you want to delete country "${deleteConfirmation.country.country_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllClientPocs;
